import { useState, useEffect, useCallback, useRef } from 'react'
import merlinImg from '../assets/merlin.png'

// ============================================================
// GUIDED TOUR  -  Spotlight tutorial component
// ============================================================
// - Scroll-aware: follows the target element on scroll/resize
// - Accepts steps via props (reusable across pages)
// - Rich tooltips with title, text, optional tip
// - Progress dots + prev/next/skip
// - Keyboard support (Escape to skip)
// ============================================================

// Resout un selecteur en privilegiant l'element VISIBLE quand plusieurs
// matchent (ex: nav desktop `hidden md:flex` + nav mobile `md:hidden` ont le
// meme data-tour : on veut celui qui a une vraie bounding box a l'ecran).
function resolveVisible(selector) {
  const els = document.querySelectorAll(selector)
  if (!els.length) return null
  if (els.length === 1) return els[0]
  for (const el of els) {
    const r = el.getBoundingClientRect()
    if (r.width > 0 && r.height > 0) return el
  }
  return els[0]
}

export default function SpotlightTutorial({ steps = [], onFinish, onAction, onStepChange, startStep = 0 }) {
  const [step, setStep] = useState(startStep)
  const [holeStyle, setHoleStyle] = useState({})
  const [tooltipStyle, setTooltipStyle] = useState({})
  const [visible, setVisible] = useState(true)
  const [transitioning, setTransitioning] = useState(false)
  const scrolledStepRef = useRef(-1)
  const rafRef = useRef(null)

  // Skip automatique des étapes marquées skipIfMissing quand leur target n'existe pas.
  // Permet au même tuto de s'adapter quand des sections conditionnelles sont absentes
  // (ex: hero IG si déjà connecté, stratégie si pas encore de recherche lancée).
  useEffect(() => {
    if (!steps[step]) return
    const s = steps[step]
    // Action d'entrée (ouvrir une fiche, afficher une démo...) : le tuto
    // GUIDE dans le vrai produit, il ne se contente pas de pointer.
    try { s.onEnter?.() } catch { /* no-op */ }
    if (s.skipIfMissing && s.target && typeof s.target === 'string') {
      // waitTarget : la cible apparaît après une action (modal qui s'ouvre,
      // fiche qui charge) → on lui laisse jusqu'à 8 s avant de skipper.
      const deadline = Date.now() + (typeof s.waitTarget === 'number' ? s.waitTarget : s.waitTarget ? 8000 : 0)
      let cancelled = false
      const check = () => {
        if (cancelled) return
        const el = resolveVisible(s.target)
        if (el) { positionSpotlight(); return }
        if (Date.now() < deadline) { setTimeout(check, 120); return }
        if (step >= steps.length - 1) { onFinish?.(); setVisible(false) }
        else setStep(step + 1)
      }
      check()
      return () => { cancelled = true }
    }
  }, [step, steps])

  const currentStep = steps[step]

  // Funnel du tuto : notifie chaque étape réellement AFFICHÉE (les skips
  // automatiques ne comptent pas).
  useEffect(() => {
    if (!currentStep || !visible) return
    try { onStepChange?.(step, currentStep) } catch { /* tracking */ }
  }, [step, visible])

  const positionSpotlight = useCallback(() => {
    if (!currentStep || !visible) return
    const el = currentStep.target
      ? (typeof currentStep.target === 'string'
        ? resolveVisible(currentStep.target)
        : currentStep.target?.current)
      : null

    if (!el) {
      // No target = centered tooltip (intro/outro step)
      setHoleStyle({ top: -9999, left: -9999, width: 0, height: 0 })
      setTooltipStyle({
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        position: 'fixed',
        zIndex: 10050,
      })
      return
    }

    const rect = el.getBoundingClientRect()
    const pad = 10
    setHoleStyle({
      top: rect.top - pad,
      left: rect.left - pad,
      width: rect.width + pad * 2,
      height: rect.height + pad * 2,
    })

    const tooltipW = 320
    const tooltipH = 250 // hauteur estimée (textes courts)
    const margin = 12

    // Layout 'bottom' : bulle en BAS de l'écran (la démo analyse occupe le
    // haut sur ~56vh, la bulle prend le tiers restant, zéro chevauchement).
    if (currentStep.layout === 'bottom') {
      setTooltipStyle({
        position: 'fixed', bottom: margin, top: 'auto', left: '50%', transform: 'translateX(-50%)',
        width: Math.min(480, window.innerWidth - 20), maxHeight: '34vh', overflowY: 'auto',
        zIndex: 10050,
      })
      return
    }

    // Layout 'top' (étapes fiche/démo) : bulle épinglée en HAUT sur ~1/3
    // d'écran, le contenu montré occupe les 2/3 du dessous, scroll libre.
    // La cible est SCROLLÉE pour que son haut arrive juste sous la bulle
    // (à ~36% de la hauteur), y compris dans une modale scrollable.
    if (currentStep.layout === 'top') {
      setTooltipStyle({
        position: 'fixed', top: margin, left: '50%', transform: 'translateX(-50%)',
        width: Math.min(480, window.innerWidth - 20), maxHeight: '34vh', overflowY: 'auto',
        zIndex: 10050,
      })
      if (el && scrolledStepRef.current !== step) {
        scrolledStepRef.current = step
        setTimeout(() => {
          try {
            const r2 = el.getBoundingClientRect()
            const delta = r2.top - Math.round(window.innerHeight * 0.36)
            let sc = el.parentElement
            while (sc && sc !== document.body) {
              const st = getComputedStyle(sc)
              if (/(auto|scroll)/.test(st.overflowY) && sc.scrollHeight > sc.clientHeight + 10) break
              sc = sc.parentElement
            }
            if (sc && sc !== document.body) sc.scrollTop += delta
            else window.scrollBy(0, delta)
          } catch { /* no-op */ }
        }, 80)
      }
      return
    }
    const vw = window.innerWidth
    const vh = window.innerHeight
    const tip = { transform: 'none', position: 'fixed', zIndex: 10050 }

    // Helper: clamp left so tooltip stays fully on screen
    const clampLeft = (l) => Math.max(margin, Math.min(l, vw - tooltipW - margin))
    const centerLeft = clampLeft(rect.left + rect.width / 2 - tooltipW / 2)

    // Smart positioning: if target center is in the bottom half → tooltip above, otherwise below
    const targetCenter = rect.top + rect.height / 2
    const isInBottomHalf = targetCenter > vh / 2

    const posHint = currentStep.position || 'auto'

    if (posHint === 'left' || posHint === 'right') {
      // Horizontal positioning
      tip.top = Math.max(margin, Math.min(rect.top + rect.height / 2 - 60, vh - 280))
      if (posHint === 'right') {
        tip.left = rect.right + pad + margin
        if (tip.left + tooltipW > vw - margin) tip.left = rect.left - pad - tooltipW - margin
      } else {
        tip.left = rect.left - pad - tooltipW - margin
        if (tip.left < margin) tip.left = rect.right + pad + margin
      }
      // If still off-screen horizontally, fall through to vertical
      if (tip.left < margin || tip.left + tooltipW > vw - margin) {
        tip.left = centerLeft
        if (isInBottomHalf) {
          tip.top = rect.top - pad - margin
          tip.transform = 'translateY(-100%)'
        } else {
          tip.top = rect.bottom + pad + margin
        }
      }
    } else {
      // Vertical positioning (auto / top / bottom)
      tip.left = centerLeft
      if (isInBottomHalf) {
        // Target is in bottom half → show tooltip ABOVE
        tip.top = rect.top - pad - margin
        tip.transform = 'translateY(-100%)'
      } else {
        // Target is in top half → show tooltip BELOW
        tip.top = rect.bottom + pad + margin
      }
    }

    // Final safety clamp
    if (typeof tip.left === 'number') {
      tip.left = Math.max(margin, Math.min(tip.left, vw - tooltipW - margin))
    }
    if (typeof tip.top === 'number') {
      // Si on est en transform translateY(-100%), la position effective est
      // tip.top - tooltipH. On clamp donc le top effectif entre margin et vh-margin.
      if (tip.transform === 'translateY(-100%)') {
        // Position effective haute = tip.top - tooltipH. Effective basse = tip.top.
        // On veut : tooltipH + margin <= tip.top <= vh - margin
        tip.top = Math.max(tooltipH + margin, Math.min(tip.top, vh - margin))
      } else {
        // Position normale : tip.top est le haut de la tooltip. Clamp pour que
        // la tooltip ENTIÈRE reste visible (tip.top + tooltipH <= vh - margin).
        tip.top = Math.max(margin, Math.min(tip.top, vh - tooltipH - margin))
      }
    }

    // Anti-chevauchement : la bulle ne doit JAMAIS cacher la zone montrée.
    // Si elle la recouvre après clamp, on la bascule du côté le plus libre.
    const effTop = () => (tip.transform === 'translateY(-100%)' ? tip.top - tooltipH : tip.top)
    const overlapsTarget = () => {
      if (typeof tip.top !== 'number') return false
      const t = effTop(), b = t + tooltipH
      return !(b < rect.top - 4 || t > rect.bottom + 4)
    }
    if (overlapsTarget()) {
      const spaceAbove = rect.top
      const spaceBelow = vh - rect.bottom
      if (spaceBelow >= tooltipH + margin * 2) {
        tip.top = rect.bottom + pad + margin
        tip.transform = 'none'
      } else if (spaceAbove >= tooltipH + margin * 2) {
        tip.top = rect.top - pad - margin
        tip.transform = 'translateY(-100%)'
      } else {
        // Cible immense : bulle collée en bas de l'écran, la zone reste visible
        tip.top = vh - tooltipH - margin
        tip.transform = 'none'
      }
    }
    // Fallback ultime : coupée hors écran → bas d'écran (jamais plein centre,
    // le centre cache ce qu'on montre)
    const effectiveTop = tip.transform === 'translateY(-100%)' ? (tip.top - tooltipH) : tip.top
    if (typeof tip.top === 'number' && (effectiveTop < 0 || effectiveTop + tooltipH > vh)) {
      tip.top = Math.max(margin, vh - tooltipH - margin)
      tip.left = centerLeft
      tip.transform = 'none'
    }

    setTooltipStyle(tip)

    // Scroll into view if needed : instantané, et UNE SEULE FOIS par étape
    // (sinon cible collée au bord d'une modale = boucle scroll/reposition
    // qui fait clignoter la bulle).
    if ((rect.top < 50 || rect.bottom > window.innerHeight - 30) && scrolledStepRef.current !== step) {
      scrolledStepRef.current = step
      el.scrollIntoView({ behavior: 'auto', block: 'center' })
      setTimeout(() => { try { positionSpotlight() } catch {} }, 60)
    }
  }, [step, currentStep, visible])

  // Position on step change + scroll/resize tracking
  useEffect(() => {
    if (!currentStep || !visible) return

    const onScrollOrResize = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(positionSpotlight)
    }

    // Initial position (small delay for DOM to settle)
    const timer = setTimeout(positionSpotlight, 40)
    window.addEventListener('scroll', onScrollOrResize, true)
    window.addEventListener('resize', onScrollOrResize)

    return () => {
      clearTimeout(timer)
      window.removeEventListener('scroll', onScrollOrResize, true)
      window.removeEventListener('resize', onScrollOrResize)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [step, currentStep, visible, positionSpotlight])

  // Keyboard support
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') handleSkip()
      if (e.key === 'Enter' || e.key === 'ArrowRight') handleNext()
      if (e.key === 'ArrowLeft' && step > 0) handlePrev()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [step, visible])

  if (!visible || !currentStep || steps.length === 0) return null

  const isLast = currentStep.last || step >= steps.length - 1

  function handleNext() {
    if (currentStep.action && onAction) {
      onAction(currentStep.action)
    }
    if (isLast) {
      onFinish?.()
      setVisible(false)
    } else {
      setTransitioning(true)
      setTimeout(() => {
        setStep(s => s + 1)
        setTransitioning(false)
      }, 100)
    }
  }

  function handleSkip() {
    onFinish?.()
    setVisible(false)
  }

  function handlePrev() {
    if (step > 0) {
      setTransitioning(true)
      setTimeout(() => {
        setStep(s => s - 1)
        setTransitioning(false)
      }, 100)
    }
  }

  const hasTarget = holeStyle.top > -999

  return (
    <div className="spotlight-overlay" onClick={currentStep.allowScroll ? undefined : handleNext}
      style={currentStep.allowScroll ? { pointerEvents: 'none' } : undefined}>
      {/* Spotlight hole */}
      {hasTarget && (
        <div
          className="spotlight-hole spotlight-hole-pulse"
          style={{ top: holeStyle.top, left: holeStyle.left, width: holeStyle.width, height: holeStyle.height }}
          onClick={e => e.stopPropagation()}
        />
      )}
      {/* Dark overlay without hole (for centered steps) */}
      {!hasTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,10,26,0.82)', zIndex: 1200 }} />
      )}

      {/* Tooltip */}
      <div
        className="spotlight-tooltip"
        style={{
          ...tooltipStyle,
          opacity: transitioning ? 0 : 1,
          transition: 'opacity .15s ease',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="spotlight-tooltip-inner" style={{
          maxWidth: 340,
          maxHeight: 'min(72vh, 620px)',
          overflowY: 'auto',
          background: 'radial-gradient(ellipse 120% 160% at 50% 120%, #1e1252 0%, #110d2e 40%, #0c0a20 100%)',
          border: '1px solid rgba(167,139,250,0.18)',
          borderRadius: 20,
          padding: '18px 20px',
          boxShadow: '0 8px 40px rgba(8,6,24,0.6), 0 0 60px rgba(139,92,246,0.1)',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Ambient orb glow */}
          <div style={{
            position: 'absolute', bottom: -20, left: -10, width: 140, height: 80,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(254,130,4,0.15) 0%, transparent 70%)',
            filter: 'blur(20px)', pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute', top: -15, right: -5, width: 120, height: 70,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 70%)',
            filter: 'blur(18px)', pointerEvents: 'none',
          }} />

          {/* Header: step counter + skip */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, position: 'relative', zIndex: 1 }}>
            <span style={{ fontSize: 10, color: 'rgba(196,181,253,0.5)', fontFamily: 'DM Mono,monospace' }}>
              {step + 1}/{steps.length}
            </span>
            <button
              onClick={handleSkip}
              style={{
                fontSize: 10, color: 'rgba(255,255,255,0.35)', background: 'none', border: 'none',
                cursor: 'pointer', fontFamily: 'DM Sans,sans-serif',
              }}
            >
              Passer le guide ✕
            </button>
          </div>

          {/* Merlin + Title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, position: 'relative', zIndex: 1 }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={{
                position: 'absolute', inset: -2, borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(255,255,255,0.85) 25%, rgba(255,255,255,0.3) 60%, transparent 80%)',
              }} />
              <img src={merlinImg} alt="Merlin" style={{
                width: 44, height: 44, objectFit: 'contain', position: 'relative', zIndex: 1,
                filter: 'drop-shadow(0 2px 10px rgba(254,130,4,0.2)) brightness(1.1)',
              }} />
            </div>
            <p style={{
              fontSize: 15, fontWeight: 700, color: 'rgba(255,255,255,0.95)',
              fontFamily: 'DM Sans,sans-serif', lineHeight: 1.3, margin: 0,
              textShadow: '0 0 20px rgba(254,130,4,0.1)',
            }}>
              {currentStep.title}
            </p>
          </div>

          {/* Description */}
          <p style={{
            fontSize: 12.5, color: 'rgba(255,255,255,0.6)', lineHeight: 1.65,
            marginBottom: currentStep.tip ? 10 : 16, fontFamily: 'DM Sans,sans-serif',
            position: 'relative', zIndex: 1,
          }}>
            {currentStep.text}
          </p>

          {/* Body riche (teasers de valeur : exemple perso, analyse vidéo) */}
          {currentStep.body && (
            <div style={{ position: 'relative', zIndex: 1, marginBottom: 14 }}>
              {currentStep.body}
            </div>
          )}

          {/* Optional tip box */}
          {currentStep.tip && (
            <div style={{
              background: 'rgba(254,130,4,0.08)',
              border: '1px solid rgba(254,130,4,0.15)',
              borderRadius: 12,
              padding: '10px 12px', marginBottom: 16, fontSize: 11.5,
              color: 'rgba(254,130,4,0.85)', lineHeight: 1.6, fontFamily: 'DM Sans,sans-serif',
              position: 'relative', zIndex: 1,
              whiteSpace: 'pre-line', // honor newlines for multi-line tips (e.g. bullet lists)
            }}>
              {currentStep.tip}
            </div>
          )}

          {/* Progress dots + nav buttons */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
            {/* Progress dots */}
            {steps.length <= 6 ? (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'nowrap', alignItems: 'center' }}>
                {steps.map((_, i) => (
                  <span
                    key={i}
                    style={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: i === step ? '#fe8204' : i < step ? '#67c931' : 'rgba(255,255,255,0.1)',
                      display: 'inline-block', transition: 'background .3s',
                      boxShadow: i === step ? '0 0 6px rgba(254,130,4,0.4)' : 'none',
                    }}
                  />
                ))}
              </div>
            ) : (
              /* 7+ étapes : barre fine (les points wrappaient à la verticale) */
              <div style={{ width: 64, height: 4, borderRadius: 99, background: 'rgba(255,255,255,0.12)', overflow: 'hidden' }}>
                <div style={{ width: `${Math.round(((step + 1) / steps.length) * 100)}%`, height: '100%', borderRadius: 99, background: 'linear-gradient(90deg, #fe8204, #f59e0b)', transition: 'width .25s ease' }} />
              </div>
            )}

            {/* Nav buttons */}
            <div style={{ display: 'flex', gap: 6 }}>
              {step > 0 && (
                <button
                  onClick={handlePrev}
                  style={{
                    padding: '7px 14px', fontSize: 12, color: 'rgba(255,255,255,0.5)',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, cursor: 'pointer',
                    fontFamily: 'DM Sans,sans-serif', fontWeight: 500,
                  }}
                >
                  ←
                </button>
              )}
              <button
                onClick={handleNext}
                style={{
                  padding: '9px 18px', fontSize: 12, color: '#fff',
                  background: isLast ? 'linear-gradient(135deg, #67c931, #52b620)' : 'linear-gradient(135deg, #fe8204, #f59e0b)',
                  border: 'none',
                  borderRadius: 10, cursor: 'pointer',
                  fontFamily: 'DM Sans,sans-serif', fontWeight: 600,
                  boxShadow: isLast ? '0 2px 12px rgba(103,201,49,0.35)' : '0 2px 12px rgba(254,130,4,0.35)',
                  whiteSpace: 'nowrap', // le CTA long ("Ouvrir mon 1er format") ne se replie plus sur 4 lignes
                }}
              >
                {currentStep.action
                  ? (currentStep.actionLabel || 'Continuer →')
                  : isLast
                    ? 'Compris !'
                    : 'Suivant →'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
